import React from 'react'
import PropTypes from 'prop-types'

import TextField from '@material-ui/core/TextField'

import { createMuiTheme } from '@material-ui/core'
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles'

// mark the required asterixes in red - see https://stackoverflow.com/questions/54936644/how-to-change-the-asterisk-color-in-required-field
const formLabelsTheme = createMuiTheme({
  overrides: {
    MuiFormLabel: {
      asterisk: {
        color: '#db3131',
        '&$error': {
          color: '#db3131'
        }
      }
    }
  }
})

export default function HotkeyEditorTextField (props) {
  const {
    label,
    required = false,
    initialValue,
    helperText = '',
    placeholder,
    onChange,
    onValidationStatusChange,
    validation,
    errorText = 'Validation failed',
    disabled,
    ...other
  } = props

  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState(false)
  const [componentHelperText, setComponentHelperText] = React.useState(helperText)
  const [validationStatus, setValidationStatus] = React.useState(!required) // initially, if the field is not required, then we can assume it is empty, and therefore valid

  const [isFirstRender, setFirstRender] = React.useState(true)

  React.useEffect(() => {
    setFirstRender(false)
  })

  // initial setup of state - this will set the validation status of the component and return it to the parent
  React.useEffect(() => {
    if (initialValue) {
      setValue(initialValue)

      processValidation(initialValue)

      onChange(initialValue)
    }
  }, [initialValue])

  const onValueChange = (event) => {
    setValue(event.target.value)

    onChange(event.target.value)
  }

  React.useEffect(() => {
    onValidationStatusChange(validationStatus)
  }, [validationStatus])

  React.useEffect(() => {
    // we don't want to fire the validation as soon as the dialog loads, but only on subsequent changes to the disabled state
    if (!isFirstRender) {
      processValidation(value)
    }
  }, [disabled])

  const onBlur = (event) => {
    processValidation(event.target.value)

    // there seems to be an issue where if onBlur is set on the HotkeyEditorTextField element, this onBlur event doesn't seem to trigger
    // It may be because the TextField child element has already lost focus? I'm not sure. To be safe, best not to define onBlur on parent
    if (other.onBlur) {
      other.onBlur(event)
    }
  }

  const processValidation = (valueToValidate) => {
    if (disabled) {
      // validation is always true if the field is disabled
      setError(false)
      setComponentHelperText(helperText)
      setValidationStatus(true)

      return
    }

    if (typeof validation === 'string') {
      const regex = new RegExp(validation)

      if (valueToValidate.match(regex)) {
        setError(false)
        setComponentHelperText(helperText)
        setValidationStatus(true)
      } else {
        setError(true)
        setComponentHelperText(errorText)
        setValidationStatus(false)
      }
    } else {
      console.log('about to call validation function on:', valueToValidate)
      const validationError = validation(valueToValidate)

      if (validationError) {
        setError(true)
        setComponentHelperText(validationError)
        setValidationStatus(false)
      } else {
        setError(false)
        setComponentHelperText(helperText)
        setValidationStatus(true)
      }
    }
  }

  return (
    <MuiThemeProvider theme={formLabelsTheme}>
      <TextField
        required={required}
        label={label}
        value={value}
        onChange={onValueChange}
        onBlur={onBlur}
        placeholder={placeholder}
        error={error}
        helperText={componentHelperText}
        disabled={disabled}
        {...other}
      />
    </MuiThemeProvider>
  )
}

HotkeyEditorTextField.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  validation: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired, // can either be a regex pattern to match, or a custom function that returns null if validated successfully, otherwise an error string to display
  errorText: PropTypes.string,
  initialValue: PropTypes.string, // if supplied, must be something that passes validation, else the control will display failed validation state
  onValidationStatusChange: PropTypes.func.isRequired,
  helperText: PropTypes.string,
  disabled: PropTypes.bool
}
